import { useMemo, useState } from "react";
import { Loader2, Users } from "lucide-react";
import OrganizationCard from "@/domains/accounts/components/OrganizationCard";
import SearchBar from "@/shared/components/SearchBar";
import ContainerLayout from '@/shared/components/ContainerLayout';
import EmptyView from "@/shared/components/EmptyView";
import { useUser } from "@/contexts/UserContext";
import { useInfiniteOrganizations } from "@/domains/accounts/services/accounts";
import { Organization } from "@/shared/types/userTypes";
import { AccountDetailsTab } from '@/shared/config/account-details-tabs';
import { Button } from "@/shared/ui/button";
import { useRouteNavigation } from "@/shared/hooks/use-route-navigation";
import { Separator } from '@/shared/ui/separator';

const AccountsPage = () => {
  const { selectedRegionCode } = useUser();
  const { switchOrganizationAndNavigateToAccountDetails, isSwitchingOrganizationContext } = useRouteNavigation();

  const {
    data: organizationsInfiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch: refetchOrganizations,
  } = useInfiniteOrganizations(true, selectedRegionCode);

  const organizations = useMemo(() => {
    const pages = organizationsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: Organization[] = [];
    for (const page of pages) {
      for (const a of page.results ?? []) {
        if (!seen.has(a.organizationId)) {
          seen.add(a.organizationId);
          merged.push(a);
        }
      }
    }
    return merged;
  }, [organizationsInfiniteData?.pages]);

  const totalCount =
    organizationsInfiniteData?.pages[0]?.totalCount ?? organizations.length;

  const [searchTerm, setSearchTerm] = useState("");

  const filteredOrganizations = useMemo(() => {
    if (!organizations.length) return [];
    const q = searchTerm.toLowerCase();
    if (!q) return organizations;
    return organizations.filter(
      (organization) =>
        organization.organizationId?.toString().toLowerCase().includes(q) ||
        organization.name?.toString().toLowerCase().includes(q)
    );
  }, [organizations, searchTerm]);

  const onManageOrganization = async (organization: Organization) => {
    switchOrganizationAndNavigateToAccountDetails({ organization, accountDetailsTab: AccountDetailsTab.DASHBOARD });
  };

  return (
    <ContainerLayout shouldShowSidebar={true} shouldAllowRegionChange={true} shouldAllowAccountChange={false}>
      <div className="flex flex-col flex-1 p-4 space-y-4">
        {/* Fixed Header Section */}
        <div className="p-4 flex items-center justify-between rounded-lg bg-muted">
          <div>
            <h1 className="text-3xl font-bold">Accounts</h1>
            <p className="text-lg">Manage accounts for <span className="font-bold text-blue-500">{selectedRegionCode}</span> datapool and their resources</p>
          </div>
        </div>
        {/* Search Bar */}
        {organizations && organizations.length > 0 && (
          <div className="space-y-4">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search accounts by name..."
            />
          </div>
        )}

        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading accounts...</p>
          </div>
        ) : (
          error ? (
            <EmptyView
              icon={Users}
              title="Error Loading Accounts"
              description="Error loading accounts at the moment. Please try again."
              actionLabel="Retry"
              onAction={refetchOrganizations}
            />
          ) : (
            <>
              {isSwitchingOrganizationContext && (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <p className="text-sm">Switching Organization...</p>
                </div>
              )}

              {/* Scrollable Content Section */}
              {filteredOrganizations && filteredOrganizations.length > 0 ? (
                <div className="flex-1 overflow-y-auto space-y-4">
                  {filteredOrganizations.map((organization) => (
                    <OrganizationCard
                      key={organization.organizationId}
                      organization={organization}
                      onManageOrganization={() => onManageOrganization(organization)}
                    />
                  ))}
                  <div className="glass-panel flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between rounded-lg">
                    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                      <span className="text-md">
                        Loaded <span className="font-medium">{organizations.length}</span>
                        {" "}
                        of <span className="font-bold">{totalCount}</span> accounts
                      </span>
                      {filteredOrganizations.length !== organizations.length && (
                        <span className="text-sm">
                          ({filteredOrganizations.length} match current search query)
                        </span>
                      )}
                    </div>
                    {hasNextPage ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-2 self-start sm:self-auto"
                        disabled={isFetchingNextPage}
                        onClick={() => void fetchNextPage()}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          "Load more"
                        )}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <EmptyView
                  icon={Users}
                  title={searchTerm ? "No Matching Accounts Found" : "No Accounts Found"}
                  description={searchTerm ? "Try adjusting your search criteria to find what you're looking for." : "There are no accounts available."}
                />
              )}
            </>
          ))}
      </div>
    </ContainerLayout >
  );
};

export default AccountsPage; 